import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const HolographicPortrait = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const skillParticles = [
    { name: 'React', x: 20, y: 30, color: '#00d4ff', delay: 0 },
    { name: 'Node.js', x: 80, y: 20, color: '#00ff88', delay: 0.2 },
    { name: 'TypeScript', x: 15, y: 70, color: '#8844ff', delay: 0.4 },
    { name: 'Python', x: 85, y: 60, color: '#ffaa00', delay: 0.6 },
    { name: 'AWS', x: 60, y: 15, color: '#ff6b35', delay: 0.8 },
    { name: 'Docker', x: 25, y: 85, color: '#00d4ff', delay: 1.0 }
  ];

  useEffect(() => {
    const handleMouseMove = (e) => {
      const rect = e.currentTarget?.getBoundingClientRect();
      if (rect) {
        setMousePosition({
          x: ((e.clientX - rect.left) / rect.width) * 100,
          y: ((e.clientY - rect.top) / rect.height) * 100
        });
      }
    };

    const container = document.getElementById('portrait-container');
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      return () => container.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  return (
    <div className="relative w-full max-w-2xl mx-auto">
      <div 
        id="portrait-container"
        className="relative aspect-square rounded-2xl overflow-hidden glass-morphism cyber-border group cursor-pointer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Main Portrait */}
        <div className="relative w-full h-full">
          <Image
            src="/assets/images/Media(4).png"
            alt="Digital Architect Portrait"
            className="w-full h-full object-cover"
          />
          
          {/* Holographic Overlay */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-br from-brand-cyan/20 via-transparent to-brand-purple/20"
            animate={{
              opacity: isHovered ? 0.8 : 0.4,
              background: isHovered 
                ? `linear-gradient(135deg, rgba(0,212,255,0.3) 0%, transparent 50%, rgba(136,68,255,0.3) 100%)`
                : `linear-gradient(135deg, rgba(0,212,255,0.2) 0%, transparent 50%, rgba(136,68,255,0.2) 100%)`
            }}
            transition={{ duration: 0.3 }}
          />

          {/* Scan Lines Effect */}
          <motion.div
            className="absolute inset-0 opacity-20"
            style={{
              background: `repeating-linear-gradient(
                0deg,
                transparent,
                transparent 2px,
                rgba(0,212,255,0.1) 2px,
                rgba(0,212,255,0.1) 4px
              )`
            }}
            animate={{
              y: isHovered ? [0, -20, 0] : [0, -10, 0]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "linear"
            }}
          />

          {/* Floating Skill Particles */}
          {skillParticles.map((particle, index) => (
            <motion.div
              key={particle.name}
              className="absolute w-3 h-3 rounded-full"
              style={{
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                backgroundColor: particle.color,
                boxShadow: `0 0 20px ${particle.color}40`
              }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{
                scale: isHovered ? [1, 1.5, 1] : [0.8, 1.2, 0.8],
                opacity: [0.6, 1, 0.6],
                x: isHovered ? (mousePosition.x - particle.x) * 0.1 : 0,
                y: isHovered ? (mousePosition.y - particle.y) * 0.1 : 0
              }}
              transition={{
                duration: 2 + particle.delay,
                repeat: Infinity,
                delay: particle.delay,
                ease: "easeInOut"
              }}
            >
              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="text-xs font-mono text-foreground bg-background/80 px-2 py-1 rounded whitespace-nowrap">
                  {particle.name}
                </span>
              </div>
            </motion.div>
          ))}

          {/* Status Indicators */}
          <div className="absolute top-4 right-4 flex flex-col space-y-2">
            <motion.div
              className="flex items-center space-x-2 bg-background/80 backdrop-blur-sm rounded-lg px-3 py-2"
              animate={{ scale: isHovered ? 1.05 : 1 }}
              transition={{ duration: 0.2 }}
            >
              <div className="w-2 h-2 bg-brand-green rounded-full animate-pulse"></div>
              <span className="text-xs font-mono text-brand-green">ONLINE</span>
            </motion.div>
            
            <motion.div
              className="flex items-center space-x-2 bg-background/80 backdrop-blur-sm rounded-lg px-3 py-2"
              animate={{ scale: isHovered ? 1.05 : 1 }}
              transition={{ duration: 0.2, delay: 0.1 }}
            >
              <Icon name="Zap" size={12} className="text-brand-cyan" />
              <span className="text-xs font-mono text-brand-cyan">ACTIVE</span>
            </motion.div>
          </div>

          {/* Neural Network Lines */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none">
            <defs>
              <linearGradient id="neuralGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#8844ff" stopOpacity="0.6" />
              </linearGradient>
            </defs>
            {skillParticles.map((particle, index) => {
              const nextParticle = skillParticles[(index + 1) % skillParticles.length];
              return (
                <motion.line
                  key={`line-${index}`}
                  x1={`${particle.x}%`}
                  y1={`${particle.y}%`}
                  x2={`${nextParticle.x}%`}
                  y2={`${nextParticle.y}%`}
                  stroke="url(#neuralGradient)"
                  strokeWidth="1"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{
                    pathLength: isHovered ? 1 : 0.3,
                    opacity: isHovered ? 0.8 : 0.3
                  }}
                  transition={{
                    duration: 1.5,
                    delay: index * 0.2,
                    ease: "easeInOut"
                  }}
                />
              );
            })}
          </svg>
        </div>

        {/* Holographic Frame */}
        <div className="absolute inset-0 rounded-2xl border-2 border-brand-cyan/50 pointer-events-none">
          <div className="absolute -top-1 -left-1 w-6 h-6 border-t-2 border-l-2 border-brand-cyan rounded-tl-lg"></div>
          <div className="absolute -top-1 -right-1 w-6 h-6 border-t-2 border-r-2 border-brand-cyan rounded-tr-lg"></div>
          <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-2 border-l-2 border-brand-cyan rounded-bl-lg"></div>
          <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-2 border-r-2 border-brand-cyan rounded-br-lg"></div>
        </div>
      </div>

      {/* Identity Matrix Display */}
      <motion.div
        className="mt-6 text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <h2 className="font-orbitron font-bold text-2xl text-foreground mb-2">
          Lwando Ntlemeza
        </h2>
        <div className="flex items-center justify-center space-x-4 text-sm font-mono">
          <span className="text-brand-cyan">Mentor</span>
          <div className="w-1 h-1 bg-brand-green rounded-full animate-pulse"></div>
          <span className="text-brand-purple">Innovator</span>
          <div className="w-1 h-1 bg-brand-green rounded-full animate-pulse"></div>
          <span className="text-brand-green">Trailblazer</span>
        </div>
      </motion.div>
    </div>
  );
};

export default HolographicPortrait;